import React, {
    useCallback, memo, useContext, useState, useEffect
} from 'react'
import { Link } from 'react-router-dom'
import { FiBell, FiHome } from 'react-icons/fi'
import * as icons from 'react-icons/fa'
import { FaStickyNote } from 'react-icons/fa'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { toast } from 'react-toastify'

import Modal from '../Modal'
import { GlobalContext } from '../../contexts/GlobalContext'
import {
    Container, Content, Profile, AreaNotificacoes, Dropdown, RecadoContainer
} from './styles'

import history from '../../services/history'
import avatarDefault from '../../assets/images/avatar.png'
import positronic from '../../assets/images/positronic horizontal.png'
import { api, authHeaders } from '../../services/api'
import stringUtils from '../../util/string'
import { socket } from '../../util/socket'
import Cart from '../Cart'

const profileNameMaxLength = 11

const Header = memo(({ usuario }) => {
    const { user, setUser } = useContext(GlobalContext)

    const [listaNotificacoesVisible, setListaNotificacoesVisible] = useState(false)
    const [closeNotificacoesListaOnClickOutside, setCloseNotificacoesListaOnClickOutside] = useState(false)
    const [notificacoes, setNotificacoes] = useState([])
    const [quantidadeNotificacoesNaoLidas, setQuantidadeNotificacoesNaoLidas] = useState(0)
    const [notificacoesVerMaisVisible, setNotificacoesVerMaisVisible] = useState(false)
    const [openNotificacao, setOpenNotificacao] = useState(null)
    const [showProfileMenu, setShowProfileMenu] = useState(false)

    async function loadNotificacoes() {
        try {
            const response = await api.get('notificacao', authHeaders())

            setNotificacoes(response.recados.map(recado => ({
                ...recado,
                remetente: {
                    id: recado.remetente?.id || 1,
                    nome: recado.remetente?.nome ? stringUtils.reduceName(recado.remetente.nome) : 'Sistema'
                },
                destinatario: {
                    id: recado.destinatario.id,
                    nome: stringUtils.reduceName(recado.destinatario.nome)
                },
                mensagem: recado.mensagem || recado.tipo.mensagem,
                icone: recado.tipo.icone
            })))

            setQuantidadeNotificacoesNaoLidas(response.nao_lidos)

            setNotificacoesVerMaisVisible(response.total_recados > response.recados.length)
        } catch (e) {
            console.error('Erro ao atualizar as notificações.')
        }
    }

    async function loadNotificacoesMaisAntigas() {
        try {
            const response = await api.get('notificacao', {
                params: {
                    recados_visiveis: notificacoes.length
                },
                ...authHeaders()
            })

            setNotificacoes(response.recados.map(recado => ({
                ...recado,
                remetente: {
                    id: recado.remetente?.id || 1,
                    nome: recado.remetente?.nome ? stringUtils.reduceName(recado.remetente.nome) : 'Syndi'
                },
                destinatario: {
                    id: recado.destinatario.id,
                    nome: stringUtils.reduceName(recado.destinatario.nome)
                },
                mensagem: recado.mensagem || recado.tipo.mensagem,
                icone: recado.tipo.icone
            })))

            setQuantidadeNotificacoesNaoLidas(response.nao_lidos)

            setNotificacoesVerMaisVisible(response.total_recados > response.recados.length)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    async function handleLerRecado(notificacao) {
        setOpenNotificacao(notificacao)

        try {
            await api.patch(`notificacao/${notificacao.id}`, {}, authHeaders())

            const currentRecado = notificacoes.find(item => item.id === notificacao.id)

            currentRecado.visto = true

            const posicao = notificacoes.map(item => item.id).indexOf(notificacao.id)

            setNotificacoes(old => [
                ...old.slice(0, posicao),
                currentRecado,
                ...old.slice(posicao + 1)
            ])

            setQuantidadeNotificacoesNaoLidas(old => old > 0 ? old - 1 : 0)

            setListaNotificacoesVisible(false)
        } catch (e) {
            toast.error(e.msg)
        }
    }

    const handleLogout = useCallback(() => {
        sessionStorage.removeItem(process.env.REACT_APP_SESSION_STORAGE_TOKEN)
        sessionStorage.removeItem(process.env.REACT_APP_SESSION_STORAGE_USER)
        localStorage.removeItem('sigas:carrinho')

        setUser(null)

        socket.disconnect()

        history.push('/login')

        window.location.reload()
    }, [])

    useEffect(() => {
        if (listaNotificacoesVisible && !closeNotificacoesListaOnClickOutside) {
            setCloseNotificacoesListaOnClickOutside(true)

            document.addEventListener('click', e => {
                const trigger = document.querySelector('.lista-avisos-trigger')
                const lista = document.querySelector('.recados-lista')
                const modal = document.querySelector('.ReactModal__Overlay')

                if (!lista?.contains(e.target) && !trigger?.contains(e.target) && !modal?.contains(e.target)) {
                    setListaNotificacoesVisible(false)
                }
            })
        }
    }, [listaNotificacoesVisible, closeNotificacoesListaOnClickOutside])

    useEffect(() => {
        if (showProfileMenu) {
            document.addEventListener('click', e => {
                const opcoesUsuario = document.querySelector('.user-options')
                const profileTrigger = document.querySelector('.profile-trigger')

                if (!opcoesUsuario?.contains(e.target) && !profileTrigger?.contains(e.target)) {
                    setShowProfileMenu(false)
                }
            })
        }
    }, [showProfileMenu])

    useEffect(() => {
        if (user) {
            loadNotificacoes()

            function afterNovaNotificacao() {
                loadNotificacoes()
            }

            socket.addListener('nova_notificacao', afterNovaNotificacao)

            return () => {
                socket.removeListener('nova_notificacao', afterNovaNotificacao)
            }
        }
    }, [user])

    const foto = user?.avatar || user?.pessoa.documentos?.find(doc => doc.tipo_documento_id === 15)?.arquivo?.link
    const authProvider = user?.auth_provider || 'default'

    return (
        <>
            <Container className="bg-slate-100 print:hidden">
                <Content>
                    <div>
                        <Link to="/dashboard" className="p-2 md:p-0 ml-3">
                            <img src={positronic} alt="Home" className="object-contain h-9 md:h-11 hidden sm:block" />

                            <FiHome size={26} className="sm:hidden" />
                        </Link>
                    </div>

                    <aside>
                        <AreaNotificacoes
                            className="hover:bg-slate-200 cursor-pointer"
                            onClick={() => { setListaNotificacoesVisible(old => !old) }}
                        >
                            <FiBell size={28} className="text-slate-600 w-7 h-7 sm:w-6 sm:h-6" />

                            {quantidadeNotificacoesNaoLidas > 0 && (
                                <div className="badge">
                                    <span>{quantidadeNotificacoesNaoLidas}</span>
                                </div>
                            )}
                        </AreaNotificacoes>

                        <Cart
                            dropdownClassNames="top-16"
                        />

                        {listaNotificacoesVisible && (
                            <Dropdown>
                                {notificacoes.map((recado, index) => (
                                    <div
                                        key={recado.id}
                                        className={`item animated fadeIn fast delay-${100 * index}ms hover:bg-slate-100 p-3`}
                                        onClick={() => { handleLerRecado(recado) }}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        {React.createElement(recado.icone ? icons[recado.icone] : FaStickyNote, {
                                            size: 20,
                                            color: recado.visto ? '#777' : 'gold'
                                        })}

                                        <b>{recado.remetente?.nome || 'Syndi'}</b>

                                        <span className="info" dangerouslySetInnerHTML={{ __html: recado.mensagem || recado.tipo.descricao }} />

                                        <span className="datetime">{format(new Date(recado.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</span>
                                    </div>
                                ))}

                                {notificacoesVerMaisVisible && (
                                    <div
                                        className="item ver-mais"
                                        onClick={loadNotificacoesMaisAntigas}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <span>Ver recados mais antigos</span>
                                    </div>
                                )}
                            </Dropdown>
                        )}

                        <Profile
                            onClick={() => {
                                setShowProfileMenu(old => !old)
                            }}
                            className="profile-trigger w-fit flex gap-3 hover:bg-slate-200 cursor-pointer select-none pr-4 pl-4"
                        >
                            <strong className="text-slate-800 w-fit xl:hidden">{usuario?.pessoa.nome.length > profileNameMaxLength ? `${usuario?.pessoa.nome.substr(0, profileNameMaxLength)}...` : usuario?.pessoa.nome}</strong>
                            <strong className="text-slate-800 w-fit hidden xl:block">{usuario?.pessoa.nome.length > profileNameMaxLength ? `${usuario?.pessoa.nome.substr(0, 16)}...` : usuario?.pessoa.nome}</strong>

                            <img className="object-cover rounded-full w-10 h-10 shadow-sm" src={foto || avatarDefault} />
                        </Profile>

                        {showProfileMenu && (
                            <div
                                className="user-options absolute top-16 right-0 sm:right-1 bg-slate-100 w-full sm:w-fit sm:min-w-48 flex flex-col gap-1 rounded-md text-slate-800 animated zoomIn faster"
                                style={{ boxShadow: 'rgba(0, 0, 0, 0.14) 0px 2px 2px 0px, rgba(0, 0, 0, 0.12) 0px 3px 1px -2px, rgba(0, 0, 0, 0.2) 0px 1px 5px 0px', zIndex: 2 }}
                            >
                                {authProvider !== 'google' && (
                                    <>
                                        <Link to="/perfil" className="border-b border-b-slate-200 text-slate-800 py-6 px-8 sm:py-3 px-4">
                                            Perfil de usuário
                                        </Link>

                                        <Link to="/acesso" className="border-b border-b-slate-200 text-slate-800 py-6 px-8 sm:py-3 px-4">
                                            Opções de acesso
                                        </Link>
                                    </>
                                )}

                                <span onClick={handleLogout} className="py-6 px-8 sm:py-3 px-4 cursor-pointer hover:text-sky-600 flex justify-between text-red-600 items-center">
                                    Sair
                                    <icons.FaSignOutAlt size={15} className="" />
                                </span>
                            </div>
                        )}
                    </aside>
                </Content>
            </Container>

            <Modal
                isOpen={!!openNotificacao}
                handleClose={() => { setOpenNotificacao(null) }}
                title="Notificação"
            >
                {openNotificacao ? (
                    <RecadoContainer>
                        <div dangerouslySetInnerHTML={{ __html: `Recebido em: <b>${format(new Date(openNotificacao?.created_at), 'dd/MM/yyyy HH:mm\'h\'')}</b>` }} />

                        <div className="message-container bg-slate-200 text-slate-800" dangerouslySetInnerHTML={{ __html: openNotificacao?.mensagem }} />

                        <div className="button-container">
                            {openNotificacao?.link && (
                                <>
                                    {openNotificacao.link.startsWith('http') ? (
                                        <a href={openNotificacao.link} className="button" target="_blank" onClick={() => { setOpenNotificacao(null) }}>Acessar</a>
                                    ) : (
                                        <Link className="button" to={openNotificacao.link} onClick={() => { setOpenNotificacao(null) }}>Acessar</Link>
                                    )}
                                </>
                            )}
                        </div>
                    </RecadoContainer>
                ) : null}
            </Modal>
        </>
    )
})

export default Header
